import {BannerItemDto} from '../../common/shared/api/gateway/BannerItemDto.interface';
import {ItemSeriesDto} from '../../common/shared/api/gateway/ItemSeriesDto.interface';
import {ItemSingleDto} from '../../common/shared/api/gateway/ItemSingleDto.interface';
import {LineBannerDto} from '../../common/shared/api/gateway/LineBannerDto.interface';
import {PromotionBannerDto} from '../../common/shared/api/gateway/PromotionBannerDto.interface';
import {ViewTypeVo} from '../../common/shared/api/karina/vo/enums/ViewTypeVo.enum';
import {StrategyCategoryItemVo} from '../../common/shared/api/karina/vo/reference/StrategyCategoryItemVo.interface';
import {ViewItemVo} from '../../common/shared/api/karina/vo/reference/ViewItemVo.type';
import {isRankingSection} from '../../pagewebLayout/isRankingSection';
import {
  BannerItem,
  CardViewItem,
  CleanViewItem,
  Item,
  ItemType,
  LineBannerItem,
  Maybe,
  MetaInfoType,
  NormalListViewItem,
  OnAirItem,
  PosterViewItem,
  PromotionBannerItem,
  RankingListViewItem,
  SectionType,
  StrategyViewItem,
  UnknownItem,
  User,
} from '../generated/types';
import {createBannerItem} from './BannerItem';
import {createBannerViewType} from './BannerViewType';
import {createCardViewItem} from './CardViewItem';
import {createCleanViewItem} from './CleanViewItem';
import {createLineBannerItem} from './LineBanner';
import {createNormalListViewItem} from './NormalListViewItem';
import {createOnAirItem} from './OnAirItem';
import {createPosterViewItem} from './PosterViewItem';
import {createPromotionBannerItem} from './PromotionBannerItem';
import {createRankingListViewItem} from './RankingListViewItem';

export function createItem(
  userInfo: Maybe<User>,
  param: {
    viewItemVo: {
      viewItem: ViewItemVo | BannerItemDto | StrategyCategoryItemVo;
      /**
       * SectionViewItemVo 내에는 타입을 결정하는 Discriminator 가 없기 때문에 view_data 와 동일레벨로 받는 view_type 으로 간주한다
       */
      viewType: ViewTypeVo;
      sectionType: SectionType;
      metaInfoType?: Maybe<MetaInfoType>;
    };
    index?: number;
  }
): Item {
  if ('viewItemVo' in param) {
    return withViewItemVo(userInfo, param.viewItemVo, param.index);
  } else {
    throw new Error('알 수 없는 타입입니다');
  }
}

function withViewItemVo(
  userInfo: Maybe<User>,
  {
    viewItem,
    viewType,
    sectionType,
    metaInfoType,
  }: {
    viewItem:
      | ViewItemVo
      | BannerItemDto
      | LineBannerDto
      | PromotionBannerDto
      | StrategyCategoryItemVo;
    /**
     * SectionViewItemVo 내에는 타입을 결정하는 Discriminator 가 없기 때문에 view_data 와 동일레벨로 받는 view_type 으로 간주한다
     */
    viewType: ViewTypeVo;
    sectionType: SectionType;
    metaInfoType?: Maybe<MetaInfoType>;
  },
  index?: number
): Item {
  if (!sectionViewItemTypeVoHandler[viewType]) {
    return {
      id: 'unknown',
      type: ItemType.Unknown,
    } as UnknownItem;
  }
  return sectionViewItemTypeVoHandler[viewType](
    userInfo,
    viewItem,
    sectionType,
    metaInfoType,
    index
  );
}

const sectionViewItemTypeVoHandler: {
  [key in ViewTypeVo]: (
    userInfo: Maybe<User>,
    viewItemVo:
      | ViewItemVo
      | BannerItemDto
      | StrategyCategoryItemVo
      | PromotionBannerDto
      | LineBannerDto,
    sectionType: SectionType,
    metaInfoType?: Maybe<MetaInfoType>,
    index?: number
  ) => Item;
} = {
  [ViewTypeVo.SeriesCardView]: (
    userInfo,
    viewItem,
    sectionType,
    metaInfoType
  ): CardViewItem => {
    return createCardViewItem(
      userInfo,
      isRankingSection(sectionType)
        ? {
            rankingItemSeriesDto: viewItem as ItemSeriesDto,
            metaInfoType,
          }
        : {
            itemSeriesDto: viewItem as ItemSeriesDto,
            metaInfoType,
          }
    );
  },
  [ViewTypeVo.SingleCardView]: (
    userInfo,
    viewItem,
    sectionType
  ): CardViewItem => {
    return createCardViewItem(userInfo, {
      itemSingleDto: viewItem as ItemSingleDto,
    });
  },
  [ViewTypeVo.SeriesPosterView]: (
    userInfo,
    viewItem,
    sectionType,
    metaInfoType
  ): PosterViewItem => {
    return createPosterViewItem(
      userInfo,
      isRankingSection(sectionType)
        ? {
            rankingItemSeriesDto: viewItem as ItemSeriesDto,
            metaInfoType,
          }
        : {
            itemSeriesDto: viewItem as ItemSeriesDto,
            metaInfoType,
            sectionType,
          }
    );
  },
  [ViewTypeVo.SinglePosterView]: (userInfo, viewItem): PosterViewItem => {
    return createPosterViewItem(userInfo, {
      itemSingleDto: viewItem as ItemSingleDto,
    });
  },
  [ViewTypeVo.SeriesCleanView]: (
    userInfo,
    viewItem,
    _,
    metaInfoType
  ): CleanViewItem => {
    return createCleanViewItem(userInfo, {
      itemSeriesDto: viewItem as ItemSeriesDto,
      metaInfoType,
    });
  },
  [ViewTypeVo.SingleCleanView]: (userInfo, viewItem): CleanViewItem => {
    return createCleanViewItem(userInfo, {
      itemSingleDto: viewItem as ItemSingleDto,
    });
  },
  [ViewTypeVo.RectangleView]: (
    userInfo,
    viewItem,
    sectionType
  ): BannerItem | OnAirItem => {
    return sectionType === SectionType.OnAir
      ? createOnAirItem({
          item: viewItem as BannerItemDto,
        })
      : createBannerItem({
          item: viewItem as BannerItemDto,
          itemType:
            sectionType && sectionType === SectionType.Event
              ? ItemType.EventBanner
              : sectionType === SectionType.Theme
                ? ItemType.StandaloneBanner
                : ItemType.Banner,
          viewType: createBannerViewType(ViewTypeVo.RectangleView),
        });
  },
  [ViewTypeVo.SquareView]: (userInfo, viewItem): BannerItem => {
    return createBannerItem({
      item: viewItem as BannerItemDto,
      itemType: ItemType.Banner,
      viewType: createBannerViewType(ViewTypeVo.SquareView),
    });
  },
  [ViewTypeVo.StrategyView]: (userInfo, viewItem): StrategyViewItem => {
    const {title, scheme, count} = viewItem as StrategyCategoryItemVo;
    return {
      id: `strategy-${title}-${scheme}-${count}`,
      type: ItemType.StrategyView,
      title,
      count,
      scheme,
    };
  },
  [ViewTypeVo.SeriesListView]: (
    userInfo,
    viewItem,
    sectionType
  ): RankingListViewItem | NormalListViewItem =>
    sectionType === SectionType.MainRanking ||
    sectionType === SectionType.Ranking
      ? createRankingListViewItem(userInfo, {
          itemSeriesDto: viewItem as ItemSeriesDto,
        })
      : createNormalListViewItem(userInfo, {
          sectionLayoutItemSeriesDto: viewItem as ItemSeriesDto,
        }),
  [ViewTypeVo.SingleListView]: (userInfo, viewItem): NormalListViewItem =>
    createNormalListViewItem(userInfo, {
      listViewLandingItemSingleDto: viewItem as ItemSingleDto,
    }),
  [ViewTypeVo.BannerPromotionView]: (
    userInfo,
    viewItemVo
  ): PromotionBannerItem =>
    createPromotionBannerItem(viewItemVo as PromotionBannerDto),
  [ViewTypeVo.BannerLineView]: (userInfo, viewItemVo): LineBannerItem =>
    createLineBannerItem(viewItemVo as LineBannerDto),
  [ViewTypeVo.BannerCardView]: (
    userInfo,
    viewItemVo,
    sectionType,
    metaInfoType
  ): CardViewItem =>
    createCardViewItem(userInfo, {
      bannerItemDto: viewItemVo as BannerItemDto,
      metaInfoType,
    }),
};
